/**
 * OC Recruitment — Case Studies
 * Design: "The Boardroom" Corporate Modernism — Boutique Retained Search Firm
 * 5 case studies demonstrating range across packaging sectors and sales leadership levels
 * Format: Role, Challenge, Approach, Outcome
 */

import { Link } from "wouter";
import { ArrowRight, MapPin, Factory } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import { pageSEO, organizationSchema } from "@/lib/seoConfig";

const caseStudies = [
  {
    id: 1,
    role: "Vice President of Sales",
    sector: "Flexible Packaging",
    location: "Midwest, United States",
    challenge:
      "A flexible packaging manufacturer was entering a new phase of growth and required an experienced Vice President of Sales to build a national commercial strategy and lead a distributed sales team. The company had previously attempted to recruit through internal networks but struggled to identify candidates with both flexible packaging expertise and proven leadership capability.",
    approach:
      "OC Recruitment conducted a retained search, mapping senior commercial leaders across flexible packaging manufacturers and converters throughout the United States. Targeted headhunting focused on individuals with experience scaling sales teams and managing national accounts within the packaging sector.",
    outcome:
      "The search resulted in the successful appointment of a Vice President of Sales with extensive industry experience and a strong network across key consumer packaged goods accounts. The placement strengthened the company's leadership team and supported the next stage of its commercial expansion.",
  },
  {
    id: 2,
    role: "Regional Sales Manager",
    sector: "Corrugated Packaging",
    location: "Southeast, United States",
    challenge:
      "A corrugated packaging manufacturer required a Regional Sales Manager to develop new business across a competitive territory. The role required a strong hunter capable of opening new accounts while managing relationships with large regional manufacturers.",
    approach:
      "OC Recruitment conducted targeted outreach to corrugated packaging sales professionals with experience selling into industrial and manufacturing accounts. The search focused on candidates with proven success in developing new business within defined geographic territories.",
    outcome:
      "The successful candidate brought strong regional relationships and a track record of opening new accounts in the packaging sector. The appointment strengthened the company's presence within the territory and supported continued revenue growth.",
  },
  {
    id: 3,
    role: "National Account Manager",
    sector: "Labels & Sleeves",
    location: "United States",
    challenge:
      "A label converter required a National Account Manager to manage and grow relationships with several large consumer packaged goods customers. The role required a commercially sophisticated account leader with experience managing complex label programs across multiple production facilities.",
    approach:
      "OC Recruitment conducted a targeted search within the label converting industry, identifying account managers with experience handling national CPG accounts. The search focused on candidates with both technical understanding of label production and strong commercial relationship management skills.",
    outcome:
      "The selected candidate brought significant experience managing national consumer packaged goods accounts and was able to quickly integrate into the client's commercial team, strengthening several key customer relationships.",
  },
  {
    id: 4,
    role: "Sales Director",
    sector: "Rigid Plastics",
    location: "West Coast, United States",
    challenge:
      "A rigid plastics manufacturer required a Sales Director to lead business development across several high-growth packaging markets including personal care and nutraceuticals. The role required both strategic leadership and direct involvement in complex customer negotiations.",
    approach:
      "OC Recruitment conducted a retained search focused on senior commercial leaders within rigid plastic packaging manufacturers. Candidates were evaluated based on leadership capability, industry relationships, and experience developing new packaging programs with major consumer brands.",
    outcome:
      "The successful placement strengthened the client's commercial leadership team and positioned the business to pursue several new growth opportunities within key packaging markets.",
  },
  {
    id: 5,
    role: "Business Development Manager",
    sector: "Metal Packaging",
    location: "United States",
    challenge:
      "A metal packaging producer required a Business Development Manager to expand its presence within the beverage sector. The role required a commercially driven sales professional capable of developing new customer relationships in a highly competitive market.",
    approach:
      "OC Recruitment conducted a focused search across metal packaging manufacturers and related packaging suppliers, targeting sales professionals with strong beverage industry connections and a proven ability to open new accounts.",
    outcome:
      "The successful candidate brought an established network within the beverage packaging market and began developing new commercial opportunities shortly after joining the business.",
  },
];

export default function CaseStudies() {
  return (
    <div>
      <SEO
        title={pageSEO.caseStudies.title}
        description={pageSEO.caseStudies.description}
        keywords={pageSEO.caseStudies.keywords}
        canonical={pageSEO.caseStudies.canonical}
        structuredData={organizationSchema}
      />
      {/* Hero */}
      <section className="bg-midnight pt-32 pb-20">
        <div className="container">
          <div className="max-w-3xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                Case Studies
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h1 className="text-4xl sm:text-5xl text-white leading-[1.1] mb-6 font-['DM_Serif_Display',Georgia,serif]">
                Retained Search in Action
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="text-lg text-white/70 leading-relaxed">
                A selection of retained search assignments completed for
                packaging manufacturers and distributors across the United
                States.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto space-y-16">
            {caseStudies.map((study, i) => (
              <ScrollReveal key={study.id} delay={i * 0.05}>
                <article className="border-l-4 border-steel/30 pl-8 md:pl-12">
                  {/* Header */}
                  <div className="mb-8">
                    <span className="text-steel uppercase tracking-[0.15em] text-xs font-semibold">
                      Case Study {study.id}
                    </span>
                    <h2 className="text-2xl md:text-3xl font-['DM_Serif_Display',Georgia,serif] text-midnight mt-2 mb-4">
                      {study.role}{" "}
                      <span className="text-steel">— {study.sector}</span>
                    </h2>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-brand">
                      <span className="flex items-center gap-1.5">
                        <MapPin size={14} className="text-steel" />
                        {study.location}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Factory size={14} className="text-steel" />
                        {study.sector}
                      </span>
                    </div>
                  </div>

                  {/* Content Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div>
                      <h3 className="text-midnight font-semibold text-sm uppercase tracking-wider mb-3">
                        The Challenge
                      </h3>
                      <p className="text-slate-brand text-sm leading-relaxed">
                        {study.challenge}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-midnight font-semibold text-sm uppercase tracking-wider mb-3">
                        Our Approach
                      </h3>
                      <p className="text-slate-brand text-sm leading-relaxed">
                        {study.approach}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-midnight font-semibold text-sm uppercase tracking-wider mb-3">
                        The Outcome
                      </h3>
                      <p className="text-slate-brand text-sm leading-relaxed">
                        {study.outcome}
                      </p>
                    </div>
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-midnight text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl sm:text-4xl text-white mb-6 font-['DM_Serif_Display',Georgia,serif]">
                Start a Retained Search
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <p className="text-lg text-white/70 leading-relaxed mb-10">
                If you are looking to strengthen your commercial team with
                proven packaging sales talent, we are ready to have a
                conversation.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-midnight text-sm font-semibold rounded hover:bg-white/90 transition-all group"
                >
                  Hire Packaging Sales Talent
                  <ArrowRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </Link>
                <Link
                  href="/for-candidates"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white text-sm font-semibold rounded hover:bg-white/10 transition-all"
                >
                  Submit Your Resume
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
